import { merchantIcons } from '@assets/icons';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const OTP_LENGTH = 6;

const EmailVerifyOtpInput = memo(({ value, onChange, onComplete }) => {
  const inputRefs = useRef([]);

  const digits = Array.from({ length: OTP_LENGTH }, (_, index) => value[index] || '');

  const focusInput = useCallback((index) => {
    inputRefs.current[index]?.focus();
  }, []);

  const updateValue = useCallback(
    (nextValue) => {
      const sanitized = nextValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
      onChange(sanitized);
      if (sanitized.length === OTP_LENGTH) {
        onComplete?.(sanitized);
      }
      return sanitized;
    },
    [onChange, onComplete],
  );

  const handleChange = useCallback(
    (index, inputValue) => {
      const digit = inputValue.replace(/\D/g, '').slice(-1);
      const nextDigits = [...digits];
      nextDigits[index] = digit;
      const sanitized = updateValue(nextDigits.join(''));
      if (digit && index < OTP_LENGTH - 1) {
        focusInput(index + 1);
      }
      if (!digit && sanitized.length === index) {
        focusInput(Math.max(index - 1, 0));
      }
    },
    [digits, focusInput, updateValue],
  );

  const handleKeyDown = useCallback(
    (index, event) => {
      if (event.key === 'Backspace' && !digits[index] && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
      if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        event.preventDefault();
        focusInput(index + 1);
      }
    },
    [digits, focusInput],
  );

  const handlePaste = useCallback(
    (event) => {
      event.preventDefault();
      const pasted = event.clipboardData.getData('text');
      const sanitized = updateValue(pasted);
      focusInput(Math.min(sanitized.length, OTP_LENGTH - 1));
    },
    [focusInput, updateValue],
  );

  return (
    <div className="flex w-full gap-0.75 desktop:gap-1">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
          className="box-border h-11 min-w-0 flex-1 rounded-field border border-merchant-border bg-merchant-bg-subtle p-2.5 text-center text-body font-medium leading-5 desktop:leading-[26px] text-merchant-text-primary outline-none placeholder:text-merchant-text-placeholder focus:border-merchant-action focus:ring-1 focus:ring-merchant-action md:p-3 desktop:h-[50px] desktop:p-4"
        />
      ))}
    </div>
  );
});

EmailVerifyOtpInput.displayName = 'EmailVerifyOtpInput';

const VerifyCodeModal = memo(({ open, email, onClose, onVerify }) => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;

    setCode('');
    setIsSubmitting(false);
    isSubmittingRef.current = false;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const handleVerify = useCallback(async () => {
    if (code.length !== OTP_LENGTH || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onVerify?.(code);
      onClose();
    } catch {
      // Keep modal open when verification fails.
      isSubmittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  }, [code, onClose, onVerify]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 desktop:p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default border-0 bg-[rgba(17,24,39,0.55)] p-0"
        onClick={onClose}
        aria-label="Close verify code dialog"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-code-title"
        className="verify-code-modal relative flex w-full max-w-onboarding flex-col overflow-hidden rounded-field border border-merchant-border bg-white"
      >
        <div className="relative flex flex-col gap-1.5 desktop:gap-2 border-b border-merchant-border p-3 desktop:p-4">
          <h2
            id="verify-code-title"
            className="pr-8 desktop:text-[24px] text-[20px] font-semibold leading-6 desktop:leading-8 text-merchant-text-primary"
          >
            Verify Code
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 flex size-5 desktop:size-6 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 outline-none focus:outline-none"
            aria-label="Close"
          >
            <img src={merchantIcons.closeCircle} alt="" className="size-full" aria-hidden="true" />
          </button>

          <div className="desktop:text-[16px] text-sm font-medium leading-4.5 desktop:leading-6 text-merchant-text-secondary">
            <p>Enter the 6-digit code sent to</p>
            <p>{email}</p>
          </div>
        </div>

        <div className="flex flex-col px-3 desktop:px-4 py-section">
          <label className="mb-1 text-sm font-semibold leading-3.75 desktop:leading-5 text-merchant-text-secondary">
            Enter the code here
          </label>
          <EmailVerifyOtpInput value={code} onChange={setCode} />
        </div>

        <div className="border-t border-merchant-border p-3 desktop:p-4">
          <button
            type="button"
            onClick={handleVerify}
            disabled={code.length !== OTP_LENGTH || isSubmitting}
            className="w-full desktop:min-h-11 min-h-8 cursor-pointer rounded-full bg-merchant-primary px-(--space-section) py-2.5 desktop:py-3 text-sm font-semibold leading-3.75 desktop:leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
});

VerifyCodeModal.displayName = 'VerifyCodeModal';

export default VerifyCodeModal;
